import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';
import Cast from 'scratch-vm/src/util/cast';
import NormalArray from 'scratch-vm/src/data-types/dash-normal-array';
import NormalObject from 'scratch-vm/src/data-types/dash-normal-object';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import {connect} from 'react-redux';
import {getEventXY} from '../lib/touch-utils';
import {getVariableValue, setVariableValue} from '../lib/variable-utils';
import ListMonitorComponent from '../components/monitor/list-monitor.jsx';
import {Map} from 'immutable';
import Prompt from './prompt.jsx';

const messages = defineMessages({
    newItemTitle: {
        defaultMessage: 'New Item',
        description: 'Title for the prompt used to add a new item to an object monitor',
        id: 'dash.objectMonitor.newItemTitle'
    },
    newItemLabel: {
        defaultMessage: 'Enter key for new item in object.',
        description: 'Label for the prompt used to add a new item to an object monitor',
        id: 'dash.objectMonitor.newItemLabel'
    },
    keyAlreadyExists: {
        defaultMessage: 'Value with the key {key} already exists!',
        description: 'Alert shown when trying to add a duplicate key to an object monitor',
        id: 'dash.objectMonitor.keyAlreadyExists'
    }
});

const clone = obj => (Cast.isNormalArray(obj) ?
    new NormalArray(obj) :
    Cast.isNormalObject(obj) ?
        new NormalObject(obj) :
        obj.slice());
const set = (obj, indexOrKey, value) => (Array.isArray(obj) ? (obj[indexOrKey] = value) : obj.set(indexOrKey, value));
const get = (obj, indexOrKey) => (Array.isArray(obj) ? obj[indexOrKey] : obj.get(indexOrKey));

class ListMonitor extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleActivate',
            'handleDeactivate',
            'handleInput',
            'handleRemove',
            'handleKeyPress',
            'handleFocus',
            'handleAdd',
            'handleOk',
            'handleCancel',
            'handleResizeMouseDown',
            'handleNavigateDown',
            'handleNavigateTo'
        ]);

        this.state = {
            activeIndex: null,
            activeValue: null,
            prompt: false,
            draggable: props.draggable !== false,
            width: props.width || 100,
            height: props.height || 200,
            path: []
        };
    }

    getCurrentList () {
        let current = this.props.value;
        for (const key of this.state.path) {
            if (current && typeof current === 'object') {
                current = get(current, key);
            } else {
                return [];
            }
        }
        return current || [];
    }

    applyDeepUpdate (callback) {
        if (!this.props.vm.canEditCollaboration()) return;
        const {vm, targetId, id: variableId} = this.props;
        const rootValue = getVariableValue(vm, targetId, variableId);

        if (this.state.path.length === 0) {
            const newValue = callback(rootValue);
            setVariableValue(vm, targetId, variableId, newValue);
            return;
        }

        const newRoot = clone(rootValue);
        let current = newRoot;
        let parent = null;
        let lastKey = null;

        for (let i = 0; i < this.state.path.length; i++) {
            parent = current;
            lastKey = this.state.path[i];
            set(parent, lastKey, clone(parent[lastKey]));
            current = get(parent, lastKey);
        }

        set(parent, lastKey, callback(current));
        setVariableValue(vm, targetId, variableId, newRoot);
    }

    handleNavigateDown (key) {
        if (!this.props.vm.canEditCollaboration()) return;
        this.handleDeactivate();
        this.setState({
            path: this.state.path.concat([key]),
            activeIndex: null,
            activeValue: null
        });
    }

    handleNavigateTo (depth) {
        if (!this.props.vm.canEditCollaboration()) return;
        this.handleDeactivate();
        this.setState({
            path: this.state.path.slice(0, depth),
            activeIndex: null,
            activeValue: null
        });
    }

    handleActivate (index) {
        if (!this.props.vm.canEditCollaboration()) return;
        // Do nothing if activating the currently active item
        if (this.state.activeIndex === index) {
            return;
        }
        const currentList = this.getCurrentList();
        const indexOrKey = Array.isArray(currentList) ? index : currentList.keys().toArray()[index];
        this.setState({
            activeIndex: index,
            activeValue: get(currentList, indexOrKey)
        });
    }

    handleDeactivate () {
        if (!this.props.vm.canEditCollaboration()) return;
        // Submit any in-progress value edits on blur
        if (this.state.activeIndex !== null) {
            this.applyDeepUpdate(list => {
                const newList = clone(list);
                if (Array.isArray(newList)) {
                    set(newList, this.state.activeIndex, this.state.activeValue);
                } else {
                    set(newList, newList.keys().toArray()[this.state.activeIndex], this.state.activeValue);
                }
                return newList;
            });
            this.setState({activeIndex: null, activeValue: null});
        }
    }

    handleFocus (e) {
        // Select all the text in the input when it is focused.
        e.target.select();
    }

    handleKeyPress (e) {
        if (!this.props.vm.canEditCollaboration()) return;
        // Special case for tab, arrow keys and enter.
        // Tab / shift+tab navigate down / up the list.
        // Arrow down / arrow up navigate down / up the list.
        // Enter / shift+enter insert new blank item below / above.
        const currentList = this.getCurrentList();
        const activePos = this.state.activeIndex;

        let navigateDirection = 0;
        if (e.key === 'Tab') navigateDirection = e.shiftKey ? -1 : 1;
        else if (e.key === 'ArrowUp') navigateDirection = -1;
        else if (e.key === 'ArrowDown') navigateDirection = 1;
        if (navigateDirection) {
            this.handleDeactivate(); // Submit in-progress edits
            const newPos = this.wrapListIndex(
                activePos + navigateDirection, Array.isArray(currentList) ? currentList.length : currentList.size
            );
            const newIndexOrKey = Array.isArray(currentList) ? newPos : currentList.keys().toArray()[newPos];
            this.setState({
                activeIndex: newPos,
                activeValue: get(currentList, newIndexOrKey)
            });
            e.preventDefault(); // Stop default tab behavior, handled by this state change
        } else if (e.key === 'Enter') {
            this.handleDeactivate();
            if (Array.isArray(currentList)) {
                this.applyDeepUpdate(list => {
                    const newListItemValue = '';
                    const newValueOffset = e.shiftKey ? 0 : 1;
                    const newListValue = list.slice(0, activePos + newValueOffset)
                        .concat([newListItemValue])
                        .concat(list.slice(activePos + newValueOffset));
                    
                    const newIndex = this.wrapListIndex(activePos + newValueOffset, newListValue.length);
                    this.setState({
                        activeIndex: newIndex,
                        activeValue: newListItemValue
                    });
                    return newListValue;
                });
            }
        }
    }

    handleInput (e) {
        if (!this.props.vm.canEditCollaboration()) return;
        this.setState({activeValue: e.target.value});
    }

    handleRemove (e) {
        if (!this.props.vm.canEditCollaboration()) return;
        e.preventDefault(); // Default would blur input, prevent that.
        e.stopPropagation(); // Bubbling would activate, which will be handled here
        this.applyDeepUpdate(list => {
            if (Array.isArray(list)) {
                const newListValue = list.slice(0, this.state.activeIndex)
                    .concat(list.slice(this.state.activeIndex + 1));
                const newActiveIndex = Math.min(newListValue.length - 1, this.state.activeIndex);
                this.setState({
                    activeIndex: newActiveIndex,
                    activeValue: newListValue[newActiveIndex]
                });
                return newListValue;
            }
            const newListValue = new NormalObject(list);
            const key = newListValue.keys().toArray()[this.state.activeIndex];
            newListValue.delete(key);
            this.setState({activeIndex: null, activeValue: null});
            return newListValue;
            
        });
    }

    handleAdd () {
        if (!this.props.vm.canEditCollaboration()) return;
        const currentList = this.getCurrentList();
        if (Array.isArray(currentList)) {
            this.applyDeepUpdate(list => {
                const newListValue = list.concat(['']);
                this.setState({activeIndex: newListValue.length - 1, activeValue: ''});
                return newListValue;
            });
            return;
        }

        this.setState({
            prompt: true,
            draggable: false
        });
    }

    handleOk (key) {
        if (!this.props.vm.canEditCollaboration()) return;
        if (!key) {
            this.setState({prompt: false, draggable: true});
            return;
        }

        this.applyDeepUpdate(list => {
            if (!Cast.isNormalObject(list)) {
                return list;
            }

            if (list.keys().toArray()
                .includes(key)) {
                // eslint-disable-next-line no-alert
                alert(this.props.intl.formatMessage(messages.keyAlreadyExists, {key}));
                this.setState({prompt: false, draggable: true});
                return list;
            }

            const newObjectValue = new NormalObject(list).set(key, '');
            this.setState({activeIndex: newObjectValue.size - 1, activeValue: '', prompt: false, draggable: true});
            return newObjectValue;
        });
    }

    handleCancel () {
        this.setState({prompt: false, draggable: true});
    }

    handleResizeMouseDown (e) {
        this.initialPosition = getEventXY(e);
        this.initialWidth = this.state.width;
        this.initialHeight = this.state.height;

        const onPointerMove = ev => {
            const newPosition = getEventXY(ev);
            const dx = newPosition.x - this.initialPosition.x;
            const dy = newPosition.y - this.initialPosition.y;
            this.setState({
                width: Math.max(Math.min(this.initialWidth + dx, this.props.customStageSize.width), 100),
                height: Math.max(Math.min(this.initialHeight + dy, this.props.customStageSize.height), 60)
            });
        };

        const onPointerUp = ev => {
            onPointerMove(ev); // Make sure width/height are up-to-date
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            this.props.vm.runtime.requestUpdateMonitor(
                Map({
                    id: this.props.id,
                    height: this.state.height,
                    width: this.state.width
                })
            );
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    }

    wrapListIndex (index, length) {
        return (index + length) % length;
    }

    render () {
        const {
            vm, // eslint-disable-line no-unused-vars
            ...props
        } = this.props;

        const currentList = this.getCurrentList();
        let resolvedValues = [];

        if (Cast.isNormalObject(currentList)) {
            resolvedValues = currentList.entries().toArray()
                .map(([k, v]) => ({__isObjEntry: true, key: k, value: v}));
        } else if (Array.isArray(currentList)) {
            resolvedValues = currentList;
        }
        return (
            <>
                {this.state.prompt && (
                    <Prompt
                        title={this.props.intl.formatMessage(messages.newItemTitle)}
                        label={this.props.intl.formatMessage(messages.newItemLabel)}
                        defaultValue="key"
                        onOk={this.handleOk}
                        onCancel={this.handleCancel}
                        showVariableOptions={false}
                        showCloudOption={false}
                        showListMessage={false}
                        isStage={false}
                        vm={vm}
                    />
                )}
                <ListMonitorComponent
                    {...props}
                    draggable={this.state.draggable}
                    value={resolvedValues}
                    path={this.state.path}
                    activeIndex={this.state.activeIndex}
                    activeValue={this.state.activeValue}
                    height={this.state.height}
                    width={this.state.width}
                    onActivate={this.handleActivate}
                    onAdd={this.handleAdd}
                    onDeactivate={this.handleDeactivate}
                    onFocus={this.handleFocus}
                    onInput={this.handleInput}
                    onKeyPress={this.handleKeyPress}
                    onRemove={this.handleRemove}
                    onResizeMouseDown={this.handleResizeMouseDown}
                    onNavigateDown={this.handleNavigateDown}
                    onNavigateTo={this.handleNavigateTo}
                />
            </>
        );
    }
}

ListMonitor.propTypes = {
    height: PropTypes.number,
    id: PropTypes.string,
    customStageSize: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number
    }),
    targetId: PropTypes.string,
    value: PropTypes.any,
    intl: intlShape,
    vm: PropTypes.instanceOf(VM),
    width: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
    draggable: PropTypes.bool
};

const mapStateToProps = state => ({
    customStageSize: state.scratchGui.customStageSize,
    vm: state.scratchGui.vm
});

export default injectIntl(connect(mapStateToProps)(ListMonitor));
